import React, { useEffect, useState, Suspense } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Avatar, Stack
} from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Group as GroupIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Dynamic Chart.js registration and chart components for CJS/ESM interop
import { lazy } from 'react';
const Line = lazy(async () => {
  const [{ Chart, LinearScale, CategoryScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend, Filler }, mod] = await Promise.all([
    import('chart.js'),
    import('react-chartjs-2')
  ]);
  Chart.register(LinearScale, CategoryScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend, Filler);
  return { default: mod.Line };
});
const Doughnut = lazy(async () => {
  const [{ Chart, LinearScale, CategoryScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend, Filler }, mod] = await Promise.all([
    import('chart.js'),
    import('react-chartjs-2')
  ]);
  Chart.register(LinearScale, CategoryScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend, Filler);
  return { default: mod.Doughnut };
});

// If you want to keep lazy loading, use the following instead:
// const Line = React.lazy(async () => {
//   const mod = await import('react-chartjs-2');
//   await import('chart.js');
//   Chart.register(LinearScale, CategoryScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend);
//   return { default: mod.Line };
// });
// const Doughnut = React.lazy(async () => {
//   const mod = await import('react-chartjs-2');
//   await import('chart.js');
//   Chart.register(LinearScale, CategoryScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend);
//   return { default: mod.Doughnut };
// });
import Swal from 'sweetalert2';

interface Store {
  id: number;
  name: string;
  apiEndpoint: string;
}

export default function StoreDetails() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [store, setStore] = useState<Store | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [breakdownType, setBreakdownType] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const navigate = useNavigate();

  // KPIs
  const [summaryData, setSummaryData] = useState<any>(null);
  const [salesBreakdown, setSalesBreakdown] = useState<any[]>([]);
  const [latestOrders, setLatestOrders] = useState<any[]>([]);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/stores`)
      .then(res => res.json())
      .then((stores: Store[]) => {
        const found = stores.find(s => String(s.id) === String(id));
        if (!found) throw new Error("Store not found");
        setStore(found);
        return fetch(`${found.apiEndpoint}`)
          .then(res => {
            if (!res.ok) throw new Error("Failed to fetch store data");
            return res.json();
          });
      })
      .then((data) => {
        // Assume data.orders or data is an array of orders
        const allOrders = Array.isArray(data) ? data : (data.orders || []);
        setOrders(allOrders);
        processDashboardData(allOrders);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id, breakdownType]);

  function processDashboardData(allOrders: any[]) {
    // Filter valid orders
    const validOrders = allOrders.filter(o => {
      const status = (o.OrderStatus || o.orderStatus || '').toLowerCase();
      return status === 'served' || status === 'delivered' || status === 'completed' || status === 'readytoserve';
    });
    // KPIs
    const totalSales = validOrders.reduce((sum, o) => sum + Number(o.TotalAmount || o.totalAmount || 0), 0);
    const totalOrders = validOrders.length;
    let productsMap: Record<string, number> = {};
    let totalProducts = 0;
    validOrders.forEach(o => {
      const itemsJson = o.OrderItemsJson || o.orderItemsJson;
      if (itemsJson) {
        try {
          const items = typeof itemsJson === 'string' ? JSON.parse(itemsJson) : itemsJson;
          if (Array.isArray(items)) {
            items.forEach(item => {
              const qty = item.Quantity || item.quantity || 1;
              totalProducts += qty;
              const name = item.ProductName || item.productName || 'Unknown';
              const price = item.Price || item.price || 0;
              if (!productsMap[name]) productsMap[name] = 0;
              productsMap[name] += (price * qty);
            });
          }
        } catch (e) { }
      }
    });
    const topProducts = Object.entries(productsMap)
      .map(([name, sales]) => ({ productName: name, totalSales: sales }))
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 5);
    const uniqueUsers = new Set(validOrders.map(o => o.CustomerName || o.customerName)).size;
    setSummaryData({ totalSales, totalOrders, totalProducts, totalUsers: uniqueUsers, topProducts });
    // Breakdown
    const breakdown: Record<string, number> = {};
    validOrders.forEach(o => {
      const rawDate = o.CreatedDate || o.createdDate || o.OrderDate || o.orderDate;
      if (!rawDate) return;
      const orderDate = new Date(rawDate);
      const adjustedDate = new Date(orderDate.getTime() + (11 * 60 * 60 * 1000));
      let key = '';
      if (breakdownType === 'daily') {
        key = adjustedDate.toLocaleDateString('en-CA');
      } else if (breakdownType === 'monthly') {
        key = `${adjustedDate.getMonth() + 1}/${adjustedDate.getFullYear()}`;
      } else {
        const firstDay = new Date(adjustedDate.getFullYear(), 0, 1);
        const pastDays = (adjustedDate.getTime() - firstDay.getTime()) / 86400000;
        const weekNum = Math.ceil((pastDays + firstDay.getDay() + 1) / 7);
        key = `Week ${weekNum}`;
      }
      if (!breakdown[key]) breakdown[key] = 0;
      breakdown[key] += (o.TotalAmount || o.totalAmount || 0);
    });
    const breakdownData = Object.entries(breakdown).map(([key, val]) => ({
      date: key,
      week: key.replace('Week ', ''),
      month: key.split('/')[0],
      year: key.split('/')[1],
      totalSales: val
    })).sort((a, b) => {
      if (breakdownType === 'daily') return new Date(a.date).getTime() - new Date(b.date).getTime();
      return 0;
    });
    setSalesBreakdown(breakdownData);
    setLatestOrders(allOrders.sort((a, b) => new Date(b.CreatedDate || b.createdDate).getTime() - new Date(a.CreatedDate || a.createdDate).getTime()).slice(0, 10));
  }

  function handleExport() {
    if (!latestOrders.length) return;
    const header = ['Order ID', 'Date', 'Customer', 'Table', 'Amount', 'Status'];
    const rows = latestOrders.map(o => [
      o.OrderId || o.orderId,
      (o.CreatedDate || o.createdDate) ? new Date(o.CreatedDate || o.createdDate).toLocaleString() : '',
      o.CustomerName || o.customerName || '',
      o.TableNumber || o.tableNumber || '',
      o.TotalAmount || o.totalAmount || '',
      (o.OrderStatus || o.orderStatus) || ((o.IsComplete || o.isComplete) ? 'Completed' : 'Pending')
    ]);
    let csvContent = "\uFEFF" + header.join(',') + '\n' +
      rows.map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `sales_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function getChartData() {
    if (!salesBreakdown || salesBreakdown.length === 0) return { labels: [], datasets: [] };
    let labels: string[] = [];
    let data: number[] = [];
    if (breakdownType === 'daily') {
      labels = salesBreakdown.map(d => new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
      data = salesBreakdown.map(d => d.totalSales);
    } else if (breakdownType === 'weekly') {
      labels = salesBreakdown.map(d => `Week ${d.week}`);
      data = salesBreakdown.map(d => d.totalSales);
    } else {
      labels = salesBreakdown.map(d => `${d.month}/${d.year}`);
      data = salesBreakdown.map(d => d.totalSales);
    }
    return {
      labels,
      datasets: [
        {
          label: 'Sales',
          data: data,
          fill: true,
          backgroundColor: 'rgba(78, 115, 223, 0.05)',
          borderColor: '#4e73df',
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#4e73df',
        },
      ],
    };
  }

  function getTopProductsData() {
    const products = summaryData?.topProducts;
    if (!summaryData || !products) return { labels: [], datasets: [] };
    return {
      labels: products.map((p: any) => p.productName),
      datasets: [{
        data: products.map((p: any) => p.totalSales),
        backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b'],
        hoverOffset: 4
      }]
    };
  }

  const KPICard = ({ title, value, icon, color, subtext }: any) => (
    <Card sx={{ height: '100%', borderLeft: `4px solid ${color}`, boxShadow: 2, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle2" color={color} fontWeight="bold" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: '#dddfeb' }}>
            {icon}
          </Box>
        </Box>
        {subtext && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {subtext}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading && !summaryData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <div className="text-red-600">{error}</div>;
  if (!store) return <div>Store not found.</div>;

  return (
    <Box sx={{ p: 3 }}>
      <Button onClick={() => navigate(-1)} variant="outlined" sx={{ mb: 2 }}>{t('admin:back', 'Back')}</Button>
      <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
        {store.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{store.apiEndpoint}</Typography>
      {/* KPI Cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 4 }}>
        <Box flex={1}>
          <KPICard
            title={t('admin:totalSales', 'Total Sales')}
            value={typeof summaryData?.totalSales === 'number' ? `${summaryData.totalSales.toLocaleString()} ${t('admin:currency', 'YER ريال')}` : `0 ${t('admin:currency', 'YER ريال')}`}
            icon={<AttachMoneyIcon sx={{ fontSize: 40 }} />}
            color="#4e73df"
          />
        </Box>
        <Box flex={1}>
          <KPICard
            title={t('admin:totalOrders', 'Total Orders')}
            value={(summaryData?.totalOrders || 0).toLocaleString()}
            icon={<ShoppingCartIcon sx={{ fontSize: 40 }} />}
            color="#1cc88a"
          />
        </Box>
        <Box flex={1}>
          <KPICard
            title={t('admin:productsSold', 'Products Sold')}
            value={(summaryData?.totalProducts || 0).toLocaleString()}
            icon={<InventoryIcon sx={{ fontSize: 40 }} />}
            color="#36b9cc"
          />
        </Box>
        <Box flex={1}>
          <KPICard
            title={t('admin:totalUsers', 'Total Users')}
            value={(summaryData?.totalUsers || 0).toLocaleString()}
            icon={<GroupIcon sx={{ fontSize: 40 }} />}
            color="#f6c23e"
          />
        </Box>
      </Stack>
      {/* Charts Section */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ mb: 4 }}>
        <Box flex={2}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e3e6f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {t('admin:salesOverview', 'Sales Overview')}
              </Typography>
              <Box>
                {['daily', 'weekly', 'monthly'].map((type) => (
                  <Button
                    key={type}
                    size="small"
                    variant={breakdownType === type ? 'contained' : 'text'}
                    onClick={() => setBreakdownType(type as any)}
                    sx={{
                      textTransform: 'capitalize',
                      minWidth: 'auto',
                      ml: 1,
                      bgcolor: breakdownType === type ? '#4e73df' : 'transparent'
                    }}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Button>
                ))}
              </Box>
            </Box>
            <CardContent sx={{ height: 400 }}>
              <Suspense fallback={<CircularProgress />}>
                <Line
                  data={getChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {}
                      },
                      x: {
                        grid: { display: false }
                      }
                    }
                  }}
                />
              </Suspense>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e3e6f0' }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {t('admin:topProducts', 'Top Products')}
              </Typography>
            </Box>
            <CardContent sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Suspense fallback={<CircularProgress />}>
                <Doughnut
                  data={getTopProductsData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' }
                    },
                    cutout: '70%'
                  }}
                />
              </Suspense>
            </CardContent>
          </Card>
        </Box>
      </Stack>
      {/* Recent Orders Table */}
      <Card sx={{ boxShadow: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e3e6f0' }}>
          <Typography variant="h6" fontWeight="bold" color="primary">
            {t('admin:recentOrders', 'Recent Orders')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            sx={{ ml: 2 }}
          >
            {t('admin:refresh', 'Refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ bgcolor: '#4e73df', ml: 2 }}
          >
            {t('admin:generateReport', 'Generate Report')}
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f8f9fc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('admin:orderId', 'Order ID')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('admin:date', 'Date')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('admin:customer', 'Customer')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('admin:table', 'Table')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('admin:amount', 'Amount')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('admin:status', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {latestOrders.length > 0 ? (
                latestOrders.map((order) => (
                  <TableRow key={order.Id || order.id} hover>
                    <TableCell sx={{ fontWeight: 'bold', color: '#4e73df' }}>
                      #{order.OrderNumber || order.orderNumber || order.OrderId || order.orderId}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const d = new Date(order.CreatedDate || order.createdDate);
                        if (isNaN(d.getTime())) return 'Invalid Date';
                        const adjusted = new Date(d.getTime() + (11 * 60 * 60 * 1000));
                        return adjusted.toLocaleString();
                      })()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 30, height: 30, bgcolor: '#36b9cc', fontSize: 14, mr: 1 }}>
                          {(order.CustomerName || order.customerName || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        {order.CustomerName || order.customerName || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>{order.TableNumber || order.tableNumber || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {(order.TotalAmount || order.totalAmount)?.toLocaleString()} {t('admin:currency', 'YER ريال')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.OrderStatus || order.orderStatus || ((order.IsComplete || order.isComplete) ? 'Completed' : 'Pending')}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          bgcolor: (order.OrderStatus || order.orderStatus) === 'Served' || (order.IsComplete || order.isComplete) ? '#e8f5e9' : '#fff3e0',
                          color: (order.OrderStatus || order.orderStatus) === 'Served' || (order.IsComplete || order.isComplete) ? '#2e7d32' : '#ef6c00'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No recent orders found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}